import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import dayjs, { Dayjs } from 'dayjs';
import { Draft } from 'immer';
import { UserType } from 'interfaces/user';
import { ChangeEvent, createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { CATALOG_SELLER_ID } from 'services/theming';
import { RequestPolicy } from 'urql';
import { TableScreenQueryParam } from 'screens/tables-screen-input';
import type { SearchSuggestion } from 'components/SearchBar';
import { useFeatures } from 'hooks/useFeatures';
import useNumberParams from 'hooks/useNumberParams';
import useOfflineQuery from 'hooks/useOfflineQuery';
import useProfileConfig from 'hooks/useProfileConfig';
import useRefreshWhenStale from 'hooks/useRefreshWhenStale';
import { useStitchProductsNew } from 'hooks/useStitchProductsNew';
import useUser from 'hooks/useUser';
import { DEFAULT_PAGE_NUMBER } from 'utils/constants';
import {
  MarketSeller,
  MarketSellerSection,
  MarketSellerQuery,
  ProductData,
  RefetchType,
  Supply,
  useMarketSellerMetaQuery,
  useMarketSellerQuery,
  MarketType,
  SelectOption,
} from 'generated/graphql';
import { DEFAULT_LOCATION_ID, DEFAULT_PAGE_SIZE, REQUEST_DAYS, XL_PAGE_SIZE } from './product-screens';
import useProductsDates, { ProductDatesResponse } from './useProductsDates';

// SearchState exposes the search bar state for the seller market screen.
//
// When the ProductsSearch feature is on, the backend returns structured
// suggestions (label + attribute-prefixed value), the parent shows the chip's
// label inline in the search bar, and the underlying search string sent to
// the backend is `<chip value>[ <free text>]`. `inputValue` is the text that
// should be rendered inside the search bar input (free text only when a chip
// is active, the full search otherwise). `selectedSuggestion` is the active
// chip or null. `onSuggestionClick` and `onClearSuggestion` are the handlers
// for picking a chip from the dropdown and clearing the inline chip
// respectively. When the flag is off, the legacy string[] suggestions path is
// used and the new fields are no-ops; existing callers see the same shape.
export type SearchState = {
  searchValue: string;
  onSearch: (val: string) => void;
  searchSuggestions: string[] | SearchSuggestion[];
  inputValue: string;
  selectedSuggestion: SearchSuggestion | null;
  onSuggestionClick: (value: string) => void;
  onClearSuggestion: () => void;
};

export const Context = createContext<{
  isInitialFetch: boolean;
  isFetching: boolean;
  tabState: { tab: number; onTabChange: (event: ChangeEvent<{}>, newTab: number) => void };
  searchState: SearchState;
  onRefetch: (requestPolicy?: RequestPolicy, ids?: string[], type?: RefetchType) => void;
  onDataChange: (updater: (draft: Draft<MarketSellerSection | undefined>) => void) => void;
  datesState: ProductDatesResponse;
  data?: MarketSellerSection;
  metaData: MarketSeller;
  pricingLocationID: string | null;
  onPricingLocationIDChange: (locationID: string) => void;
  buyerID: number | null;
  onBuyerIDChange: (buyerID: number | null) => void;
  buyerSelectOptions: SelectOption[];
  onIncreasePage: () => void;
  isCatalogMarket: boolean;
  sellerID?: string;
  startDate: Dayjs;
} | null>(null);

const THIRTY_MINUTES = 1000 * 60 * 30; //30minutes
const STORAGE_KEY = 'productsLoadTime';

const toQueryDate = (date: Dayjs) => {
  const utcStartDate = new Date(Date.UTC(date.get('year'), date.get('month'), date.get('date'), 0, 0, 0));
  return utcStartDate.toISOString().split('T')[0] + 'T00:00:00.000Z';
};

// The meta queries resolve title, info cards, location options, notice dialog,
// market type and buyer options from the sellerID alone: the resolvers ignore
// startDate, days, locationID, search, buyerID and paginationOption, and
// `section` is resolved separately. MarketSellerInput still requires those
// fields, so they are pinned here to keep the meta variables stable. They used
// to track the products query, which re-fetched an identical meta payload on
// every scroll page, search, location switch and date change.
const META_START_DATE = toQueryDate(dayjs());
const META_PAGINATION = {
  pageNumber: DEFAULT_PAGE_NUMBER,
  pageSize: DEFAULT_PAGE_SIZE,
  ids: [],
  type: RefetchType.Unknown,
};

export const ProductsProvider = ({ children }: { children: ReactNode }) => {
  const { isEnabled } = useFeatures();
  const isProductsSearchEnabled = isEnabled('ProductsSearch');
  const isXLGridEnabled = isEnabled('ProductsXLGrid');

  const theme = useTheme();
  const isXLViewport = useMediaQuery(theme.breakpoints.up('xl'));
  const pageSize = isXLGridEnabled && isXLViewport ? XL_PAGE_SIZE : DEFAULT_PAGE_SIZE;

  /* General */
  const { profileID } = useProfileConfig();
  const { user, isPendingReview, isRegistering } = useUser();
  const [getNumberParam, setNumberParam] = useNumberParams();
  const { isAdmin } = useUser();
  const location = useLocation();

  /* Pagination */
  const [pageNumber, setPageNumber] = useState(DEFAULT_PAGE_NUMBER);

  const handleResetPage = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setPageNumber(DEFAULT_PAGE_NUMBER);
    setRefetchType(RefetchType.Unknown);
    localStorage.setItem(STORAGE_KEY, dayjs().toISOString());
  }, []);

  /* Tabs */
  const tabIndex = getNumberParam(TableScreenQueryParam.Tab) ?? 0;
  const [tab, setTab] = useState(tabIndex);

  const handleTabChange = useCallback(
    (_: ChangeEvent<{}>, newTab: number) => {
      setNumberParam(TableScreenQueryParam.Tab, newTab);
      setTab(newTab);
    },
    [setNumberParam],
  );

  const tabState = useMemo(
    () => ({
      tab,
      onTabChange: handleTabChange,
    }),
    [tab, handleTabChange],
  );

  const [searchValue, setSearchValue] = useState('');

  /* Dates  */
  // startDate, selectedDate, dateRange, dates, dateIndex, onStartDateChange
  const [supply, setSupply] = useState<Supply | undefined>();
  const datesState = useProductsDates({
    tab: tab,
    supply: supply,
    handleResetPage,
  });

  const { startDate } = datesState;

  /* Query */
  // Query related states
  const [pricingLocationID, setPricingLocationID] = useState<string | null>(DEFAULT_LOCATION_ID);
  const [refetchType, setRefetchType] = useState(RefetchType.Unknown);
  const [selectedProductIDs, setSelectedProductIDs] = useState<string[]>([]);
  const [buyerID, setBuyerID] = useState<number | null>(null);

  const queryStartDate = useMemo(() => toQueryDate(startDate), [startDate]);

  const isCatalogProductsRoute = useMemo(() => location.pathname.includes('/catalog/products'), [location.pathname]);

  const isProductsQueryPaused = UserType.Buyer === user?.userType || !profileID || isPendingReview || isRegistering;

  const variables = useMemo(
    () => ({
      sellerID: isCatalogProductsRoute ? CATALOG_SELLER_ID.toString() : (profileID ?? ''),
      startDate: queryStartDate,
      days: REQUEST_DAYS,
      search: searchValue.trim(),
      locationID: pricingLocationID ?? DEFAULT_LOCATION_ID,
      paginationOption: {
        pageNumber: pageNumber,
        pageSize: pageSize,
        ids: selectedProductIDs,
        type: refetchType,
      },
      buyerID: String(buyerID ?? 0),
    }),
    [
      profileID,
      queryStartDate,
      searchValue,
      pricingLocationID,
      pageNumber,
      refetchType,
      selectedProductIDs,
      buyerID,
      isCatalogProductsRoute,
      pageSize,
    ],
  );

  const metaVariables = useMemo(
    () => ({
      sellerID: isCatalogProductsRoute ? CATALOG_SELLER_ID.toString() : (profileID ?? ''),
      startDate: META_START_DATE,
      days: REQUEST_DAYS,
      search: '',
      locationID: DEFAULT_LOCATION_ID,
      paginationOption: META_PAGINATION,
      buyerID: '0',
    }),
    [isCatalogProductsRoute, profileID],
  );

  const queryOptions = {
    variables: { input: { ...variables } },
    requestPolicy: 'network-only' as const,
    pause: isProductsQueryPaused,
  };

  /* MetaData */
  // The -new suffixed cache keys are the ones live clients have been writing to;
  // the unsuffixed keys still hold pre-catalog data, so they stay retired.
  const [productsMeta, refetchProductsMeta] = useOfflineQuery('products-meta-query-new', useMarketSellerMetaQuery, {
    variables: { input: metaVariables },
    requestPolicy: 'network-only' as const,
    pause: isProductsQueryPaused,
  });

  const productsMetaData = useMemo(
    () => productsMeta?.data?.marketSeller as MarketSeller,
    [productsMeta?.data?.marketSeller],
  );

  const isCatalogMarket = useMemo(() => productsMetaData?.type === MarketType.Catalog, [productsMetaData?.type]);

  const [productsResult, refetchProducts] = useOfflineQuery(
    `products-query-${profileID}-new`,
    useMarketSellerQuery,
    queryOptions,
  );

  const stitchProductsNew = useStitchProductsNew(
    (productsResult?.data as MarketSellerQuery)?.marketSeller?.section as MarketSellerSection,
    refetchType === RefetchType.Unknown,
  );

  const { products: productsData, set: handleSetData, setProductSections } = stitchProductsNew;

  //If the selected profileID changes on admin, clear stitched data
  useEffect(() => {
    if (isAdmin) {
      setProductSections(undefined);
      handleResetPage();
    }
  }, [profileID, isAdmin, setProductSections, handleResetPage]);

  // If the buyer selection changes, clear stitched data so stale prices aren't shown while refetching
  useEffect(() => {
    setProductSections(undefined);
  }, [buyerID, setProductSections]);

  useEffect(() => {
    setSupply((productsData?.sections?.[0]?.productWeights?.[0]?.data?.products?.[0] as ProductData)?.supply as Supply);
  }, [productsData]);

  // Search state
  const handleSearch = useCallback(
    (val: string) => {
      setSearchValue(val);
      handleResetPage();
    },
    [handleResetPage],
  );

  const structuredSuggestions: SearchSuggestion[] = useMemo(
    () => (isProductsSearchEnabled ? (productsData?.searchSuggestions ?? []) : []),
    [isProductsSearchEnabled, productsData?.searchSuggestions],
  );

  // selectedSuggestion is derived from the current searchValue rather than
  // held as separate state. This makes URL hydration and external search
  // changes work without extra effects: whenever a chip's value is at the
  // head of the search string, that chip is treated as active.
  const selectedSuggestion = useMemo<SearchSuggestion | null>(() => {
    if (!isProductsSearchEnabled || structuredSuggestions.length === 0) return null;
    return structuredSuggestions.find((s) => searchValue === s.value || searchValue.startsWith(`${s.value} `)) ?? null;
  }, [isProductsSearchEnabled, structuredSuggestions, searchValue]);

  const freeText = useMemo(
    () => (selectedSuggestion ? searchValue.slice(selectedSuggestion.value.length).trimStart() : searchValue),
    [searchValue, selectedSuggestion],
  );

  // onSearchText is fired by the search bar when the user types. When a chip
  // is active we keep the chip prefix in the underlying search string and
  // only replace the free-text portion.
  const onSearchText = useCallback(
    (text: string) => {
      if (selectedSuggestion) {
        const next = text.length > 0 ? `${selectedSuggestion.value} ${text}` : selectedSuggestion.value;
        handleSearch(next);
        return;
      }
      handleSearch(text);
    },
    [selectedSuggestion, handleSearch],
  );

  // onSuggestionClick is fired when the user picks a chip from the dropdown.
  // The chip's value is prepended to any free text the user has already
  // typed; if a different chip was active it is replaced.
  const onSuggestionClick = useCallback(
    (value: string) => {
      if (!isProductsSearchEnabled) {
        handleSearch(value);
        return;
      }
      const matched = structuredSuggestions.find((s) => s.value === value);
      if (!matched) {
        handleSearch(value);
        return;
      }
      const next = freeText.length > 0 ? `${matched.value} ${freeText}` : matched.value;
      handleSearch(next);
    },
    [isProductsSearchEnabled, structuredSuggestions, freeText, handleSearch],
  );

  // onClearSuggestion strips only the active chip's value from the search,
  // preserving any free text the user typed alongside it.
  const onClearSuggestion = useCallback(() => {
    if (!selectedSuggestion) return;
    handleSearch(freeText);
  }, [selectedSuggestion, freeText, handleSearch]);

  const searchState = useMemo<SearchState>(
    () => ({
      searchValue,
      onSearch: isProductsSearchEnabled ? onSearchText : handleSearch,
      searchSuggestions: isProductsSearchEnabled ? structuredSuggestions : (productsData?.suggestions ?? []),
      inputValue: isProductsSearchEnabled ? freeText : searchValue,
      selectedSuggestion,
      onSuggestionClick,
      onClearSuggestion,
    }),
    [
      searchValue,
      handleSearch,
      onSearchText,
      isProductsSearchEnabled,
      structuredSuggestions,
      productsData?.suggestions,
      freeText,
      selectedSuggestion,
      onSuggestionClick,
      onClearSuggestion,
    ],
  );

  // Query related state hookes
  useEffect(() => {
    if (!pricingLocationID && productsData) {
      setPricingLocationID(productsMetaData?.locationOptions?.[0]?.value ?? DEFAULT_LOCATION_ID);
    }
  }, [pricingLocationID, productsData, productsMetaData?.locationOptions]);

  useEffect(() => {
    setSelectedProductIDs([]);
  }, [profileID, queryStartDate, searchValue, pricingLocationID, pageNumber]);

  /* Refetch  */
  const handleRefetch = useCallback(
    (requestPolicy: RequestPolicy = 'network-only', ids: string[] = [], type = RefetchType.Unknown) => {
      if (refetchType !== type || selectedProductIDs[0] !== ids[0]) {
        setRefetchType(type);
        setSelectedProductIDs(ids);
      }
      refetchProducts({ requestPolicy });
      refetchProductsMeta({ requestPolicy });
    },
    [refetchProducts, refetchProductsMeta, refetchType, selectedProductIDs],
  );

  /* Fetching state*/
  const isFetching = useMemo(
    () => productsResult?.fetching || productsMeta?.fetching,
    [productsResult?.fetching, productsMeta?.fetching],
  );

  /* Data freshness */
  const { onSetLoadTime } = useRefreshWhenStale({
    storageKey: STORAGE_KEY,
    maxAgeMs: THIRTY_MINUTES,
    onStale: handleResetPage,
  });

  useEffect(() => {
    onSetLoadTime();
  }, [productsResult, onSetLoadTime]);

  const value = useMemo(
    () => ({
      isInitialFetch: isFetching && pageNumber === DEFAULT_PAGE_NUMBER && !productsData,
      isFetching: isFetching,
      tabState,
      searchState,
      onRefetch: handleRefetch,
      onDataChange: handleSetData,
      datesState,
      data: productsData,
      metaData: productsMetaData,
      buyerSelectOptions: productsMetaData?.buyerOptions,
      pricingLocationID,
      startDate,
      sellerID: isCatalogProductsRoute ? CATALOG_SELLER_ID.toString() : profileID,
      onPricingLocationIDChange: (val: string) => {
        handleResetPage();
        setPricingLocationID(val);
      },
      buyerID,
      onBuyerIDChange: (val: number | null) => {
        handleResetPage();
        setBuyerID(val);
      },
      onIncreasePage: () => {
        setPageNumber((prev) => prev + 1);
      },
      isCatalogMarket,
    }),
    [
      tabState,
      searchState,
      datesState,
      productsData,
      productsMetaData,
      handleSetData,
      handleRefetch,
      pricingLocationID,
      startDate,
      handleResetPage,
      setPageNumber,
      pageNumber,
      isFetching,
      isCatalogMarket,
      buyerID,
      isCatalogProductsRoute,
      profileID,
    ],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useProducts = () => {
  const result = useContext(Context);
  if (!result) {
    throw new Error('useProducts must be used within ProductsProvider');
  }
  return result;
};
